<template>
  <section>
    <stepper-step
      :complete="complete"
      :title="`Select entities for ${site.hostname} in ${language.name}`"
      subtitle="Select from the below list of entities that are available for the site language."
      :step="$props.step"
      :editable="currentStep >= 4"
    />

    <v-stepper-content v-bind="$props" v-on="$listeners">
      <div class="pt-4 d-flex">
        <select-list
          :value="formattedValue"
          :is-disabled="loading"
          :is-loading="loading"
          :items="entityCombinations"
          label="Entities"
          class="mr-4 mb-4 flex-grow-1"
          hide-select-all
          hide-details
          multiple
          @input="onChange"
        />
        <v-btn
          v-if="hasMultipleLanguages"
          color="primary"
          :loading="loading"
          :disabled="formattedValue.length === 0"
          @click="onApply"
          >Apply to all</v-btn
        >
      </div>
    </v-stepper-content>
  </section>
</template>

<script>
import { VStepperContent } from 'vuetify/lib'

import cartesianProduct from '@/utils/cartesian-product'

import StepperStep from './stepper-step.vue'
import SelectList from '@/components/forms/select-list'

export default {
  name: 'StepperEntityStep',
  components: { SelectList, StepperStep },
  extends: VStepperContent,
  props: {
    currentStep: {
      type: Number,
      required: true,
    },
    value: {
      type: [Array, Object, null],
      required: true,
    },
    languageId: {
      type: String,
      required: true,
    },
    siteId: {
      type: String,
      required: true,
    },
    sites: {
      type: Array,
      required: true,
    },
    languages: {
      type: Array,
      required: true,
    },
    entityTypePageTypes: {
      type: Array,
      required: true,
    },
    entities: {
      type: Array,
      required: true,
    },
    formData: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    site() {
      return this.sites.find(({ id }) => id == this.siteId)
    },
    language() {
      return this.languages.find(({ id }) => id == this.languageId)
    },
    entityTypes() {
      return this.entityTypePageTypes
        .filter(({ page_type_id }) => this.formData.page_type_id === page_type_id)
        .map(({ entity_type_id }) => entity_type_id)
    },
    entityGroupedByEntityTypes() {
      return this.entityTypes.map(entityTypeId =>
        this.entities.filter(({ entity_type_id }) => entity_type_id === entityTypeId).map(({ id }) => id)
      )
    },
    entityCombinations() {
      return cartesianProduct(this.entityGroupedByEntityTypes).map(entityIds => {
        const entities = this.entities.filter(({ id }) => entityIds.includes(id))
        const key = entities.map(({ entity }) => entity.default.name).join(' / ')
        const value = entities.map(({ id }) => id).join('|')
        return { key, value }
      })
    },
    complete() {
      return Object.keys(this.formData.sites).every(siteId =>
        Object.keys(this.formData.sites[siteId]).every(languageId => this.formData.sites[siteId][languageId].length > 0)
      )
    },
    formattedValue() {
      return this.value.map(val => val.join('|')).flat()
    },
    hasMultipleLanguages() {
      return (
        Object.keys(this.formData.sites)
          .map(siteId => Object.keys(this.formData.sites[siteId]))
          .flat().length > 1
      )
    },
  },
  methods: {
    onChange(items) {
      this.$emit(
        'change',
        items.map(item => item.split('|'))
      )
    },
    onApply() {
      this.$emit('apply')
    },
    onContinue() {
      this.$emit('increment')
    },
  },
}
</script>
